var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import isEqual from 'lodash/isEqual';
import { useIsMounted } from '../../../../@common/hooks/use-is-mounted';
import { FieldType } from '../../../../@common/types';
import { getNodeByName } from './utils';
var getValue = function (evt) { return evt.target.value; };
var getChecked = function (evt) { return evt.target.checked; };
var getMultipleSelect = function (evt) {
    return __spreadArray([], evt.target.options, true).filter(function (el) { return el.selected; })
        .map(function (el) { return el.value; });
};
var getValueByType = new Map([
    [FieldType.BOOLEAN, getChecked],
    [FieldType.NUMBER, getValue],
    [FieldType.TEXT, getValue],
    [FieldType.SELECT_MULTIPLE, getMultipleSelect],
]);
var DEF_ERRORS = [];
export var useValidateInput = function (customRef, validators, formRef, field, type, nameFromProp, hideErrorInXSec) {
    var _a, _b;
    var _c = useState(false), mounted = _c[0], setMounted = _c[1];
    useEffect(function () {
        setMounted(true);
    }, [setMounted]);
    var name = useMemo(function () { return (field === null || field === void 0 ? void 0 : field.name) || nameFromProp; }, [field === null || field === void 0 ? void 0 : field.name, nameFromProp]);
    var getMounted = useIsMounted();
    var inputRef = useMemo(function () {
        var ERROR_MESSAGE = 'Не удалось найти ссылку на инпут. Добавьте корректное имя вашему полю' +
            ' input, или используйте inputRef';
        if (customRef.current) {
            return customRef;
        }
        if (mounted && name) {
            var ref = getNodeByName(name, formRef);
            if (ref) {
                return ref;
            }
            else {
                throw new Error(ERROR_MESSAGE);
            }
        }
        return {
            current: null,
        };
    }, [customRef, formRef, mounted, name]);
    var getFormValueByName = useCallback(function (name) {
        var foundInputRef = getNodeByName(name, formRef);
        if (foundInputRef) {
            return foundInputRef.current.type === 'checkbox'
                ? foundInputRef.current.checked
                : foundInputRef.current.value;
        }
        return null;
    }, [formRef]);
    var _d = useState(DEF_ERRORS), errors = _d[0], setErrors = _d[1];
    var handleSetErrors = useCallback(function (errors) {
        if (field === null || field === void 0 ? void 0 : field.setErrors) {
            field.setErrors(errors);
        }
        else {
            setErrors(function (s) {
                if (isEqual(s, errors)) {
                    return s;
                }
                return errors;
            });
        }
    }, [field === null || field === void 0 ? void 0 : field.setErrors, setErrors]);
    var setEmptyErrors = useCallback(function () {
        if (getMounted()) {
            handleSetErrors(DEF_ERRORS);
        }
    }, [getMounted, handleSetErrors]);
    var timeout = useRef();
    var handleFieldChanged = useCallback(function (e) {
        var _a, _b, _c;
        e.preventDefault();
        var errors = [];
        var isMultiSelect = ((_a = e.target) === null || _a === void 0 ? void 0 : _a.tagName) === 'SELECT' &&
            Boolean((_b = e.target) === null || _b === void 0 ? void 0 : _b.multiple);
        if (isMultiSelect && type !== FieldType.SELECT_MULTIPLE) {
            throw new Error('Вы используете select со сойством multiple. Укажите' +
                ' FieldType.SELECT_MULTIPLE явно для корректной работы элемента');
        }
        var hasValidation = Boolean((validators === null || validators === void 0 ? void 0 : validators.length) && e.target);
        if (hasValidation) {
            var getCurrentValue = (_c = getValueByType.get(type)) !== null && _c !== void 0 ? _c : getValue;
            var value_1 = getCurrentValue(e);
            validators.forEach(function (validate) {
                var result = validate(value_1, name, getFormValueByName);
                if (result) {
                    errors.push(result);
                }
            });
        }
        handleSetErrors(errors);
        if (hideErrorInXSec) {
            timeout.current = setTimeout(setEmptyErrors, hideErrorInXSec);
        }
    }, [
        handleSetErrors,
        setEmptyErrors,
        getFormValueByName,
        getMounted,
        name,
        type,
        validators,
        hideErrorInXSec,
    ]);
    var handleFocus = useCallback(function (e) {
        e.preventDefault();
        setEmptyErrors();
    }, [setEmptyErrors]);
    useEffect(function () {
        var input = inputRef.current;
        var hasEventHandler = Boolean(input);
        if (hasEventHandler) {
            if (input.tagName.toUpperCase() === 'SELECT') {
                input.addEventListener('change', handleFieldChanged);
            }
            input.addEventListener('blur', handleFieldChanged);
            input.addEventListener('focus', handleFocus);
        }
        return function () {
            if (hasEventHandler) {
                if (input.tagName.toUpperCase() === 'SELECT') {
                    input.removeEventListener('change', handleFieldChanged);
                }
                input.removeEventListener('blur', handleFieldChanged);
                input.removeEventListener('focus', handleFocus);
            }
        };
    }, [inputRef]);
    useEffect(function () {
        return function () {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, [timeout]);
    return {
        errors: (_a = field === null || field === void 0 ? void 0 : field.errors) !== null && _a !== void 0 ? _a : errors,
        setErrors: (_b = field === null || field === void 0 ? void 0 : field.setErrors) !== null && _b !== void 0 ? _b : setErrors,
    };
};
